import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Terminal,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USE_CASE_OPTIONS = [
  { value: "sourcing", label: "Sourcing candidates" },
  { value: "screening", label: "Screening & shortlisting" },
  { value: "interview_scheduling", label: "Interview scheduling" },
  { value: "assessment_interviewing", label: "Assessment & interviewing" },
  { value: "candidate_engagement", label: "Candidate engagement (chatbot)" },
  { value: "other", label: "Other" },
];

const IMPACT_OPTIONS = [
  { value: "time_to_hire", label: "Time-to-hire" },
  { value: "recruiter_hours", label: "Recruiter hours saved" },
  { value: "screening_volume", label: "Screening volume" },
  { value: "cost", label: "Cost" },
  { value: "candidate_quality", label: "Candidate quality" },
  { value: "no_impact", label: "No measurable impact" },
];

const BUY_AGAIN_OPTIONS = [
  { value: "definitely", label: "Definitely" },
  { value: "probably", label: "Probably" },
  { value: "probably_not", label: "Probably not" },
  { value: "no_way", label: "No way" },
];

const RATING_LABELS = {
  1: "Total miss",
  3: "Mixed",
  5: "Exceeded expectations",
};

export default function Benchmark() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefillEmail = location.state?.email || "";

  const [tool, setTool] = useState("");
  const [useCases, setUseCases] = useState([]);
  const [promiseRating, setPromiseRating] = useState(null);
  const [impactAreas, setImpactAreas] = useState([]);
  const [wouldBuyAgain, setWouldBuyAgain] = useState("");
  const [peerAdvice, setPeerAdvice] = useState(""); // Q6 — fully optional
  const [email, setEmail] = useState(prefillEmail);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (current, value) =>
    current.includes(value) ? current.filter((v) => v !== value) : [...current, value];

  // Progress: 6 required gates (Q1, Q2, Q3, Q4, Q5, email). Q6 deliberately excluded.
  const completed = useMemo(() => {
    let n = 0;
    if (tool.trim()) n++;
    if (useCases.length > 0) n++;
    if (promiseRating !== null) n++;
    if (impactAreas.length > 0) n++;
    if (wouldBuyAgain) n++;
    if (email.trim()) n++;
    return n;
  }, [tool, useCases, promiseRating, impactAreas, wouldBuyAgain, email]);

  const total = 6;
  const pct = Math.round((completed / total) * 100);
  const canSubmit =
    tool.trim() &&
    useCases.length > 0 &&
    promiseRating !== null &&
    impactAreas.length > 0 &&
    wouldBuyAgain &&
    email.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/benchmark`, {
        tool: tool.trim(),
        use_cases: useCases,
        promise_rating: promiseRating,
        impact_areas: impactAreas,
        would_buy_again: wouldBuyAgain,
        // Q6: send null when empty so backend stores null; no client-side block.
        peer_advice: peerAdvice.trim() ? peerAdvice.trim() : null,
        email: email.trim().toLowerCase(),
      });
      navigate("/welcome", { state: { email: email.trim().toLowerCase(), from: "benchmark" } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : "Could not submit. Please try again.";
      toast.error("submission failed.", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="benchmark-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 18% 12%, rgba(22,163,74,0.10), transparent 38%), radial-gradient(circle at 85% 88%, rgba(22,163,74,0.06), transparent 42%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.40]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 80%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-emerald-700"
        >
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / benchmark
        </Link>
        <Link
          to="/"
          data-testid="back-home-link"
          className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 transition-colors hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Back
        </Link>
      </header>

      {/* Sticky progress */}
      <div className="sticky top-0 z-20 border-b border-zinc-200 bg-[#F7F6F1]/90 px-6 py-3 backdrop-blur sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-4">
          <span className="shrink-0 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            {completed}/{total}
          </span>
          <div className="relative h-1 w-full overflow-hidden bg-zinc-200" data-testid="progress-bar">
            <div
              className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="hidden shrink-0 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500 sm:inline">
            ~ 1 min
          </span>
        </div>
      </div>

      <section className="relative z-10 mx-auto w-full max-w-3xl px-6 pb-24 pt-12 sm:px-10 sm:pt-16">
        <div className="reveal">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.32em] text-emerald-600">
            // ai recruiting benchmark
          </p>
          <h1
            data-testid="benchmark-title"
            className="mt-4 font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-zinc-900 sm:text-5xl"
          >
            <BrandMark size="inline" />
            <span className="text-zinc-400"> &mdash; </span>
            AI Recruiting Benchmark.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
            Six quick questions. No nonsense. Contribute your stack &mdash; see
            everyone else&apos;s when the report drops.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="benchmark-form"
          className="reveal mt-12 space-y-10 sm:space-y-12"
          style={{ animationDelay: "120ms" }}
        >
          {/* Q1 */}
          <Question
            num="01"
            label="Which AI recruiting tool did you use?"
            testId="q1-block"
          >
            <input
              data-testid="q1-tool-input"
              type="text"
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              placeholder="e.g. Paradox, HireVue, SeekOut…"
              className="w-full border border-zinc-300 bg-white px-4 py-4 font-display text-lg text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
            />
          </Question>

          {/* Q2 */}
          <Question
            num="02"
            label="What did you use it for?"
            hint="Multi-select"
            testId="q2-block"
          >
            <ChipGrid
              options={USE_CASE_OPTIONS}
              selected={useCases}
              onToggle={(v) => setUseCases(toggle(useCases, v))}
              testIdPrefix="q2"
            />
          </Question>

          {/* Q3 */}
          <Question
            num="03"
            label="Did it deliver on its main promise?"
            hint="1 = Total miss · 3 = Mixed · 5 = Exceeded expectations"
            testId="q3-block"
          >
            <RatingScale
              value={promiseRating}
              onChange={setPromiseRating}
              testIdPrefix="q3"
            />
          </Question>

          {/* Q4 */}
          <Question
            num="04"
            label="Where did you see impact?"
            hint="Multi-select"
            testId="q4-block"
          >
            <ChipGrid
              options={IMPACT_OPTIONS}
              selected={impactAreas}
              onToggle={(v) => setImpactAreas(toggle(impactAreas, v))}
              testIdPrefix="q4"
            />
          </Question>

          {/* Q5 */}
          <Question
            num="05"
            label="Would you buy it again?"
            testId="q5-block"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {BUY_AGAIN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`q5-${opt.value}`}
                  onClick={() => setWouldBuyAgain(opt.value)}
                  className={`group flex items-center justify-between border px-4 py-3 font-display text-base transition-colors ${
                    wouldBuyAgain === opt.value
                      ? "border-emerald-600 bg-emerald-50 text-zinc-900"
                      : "border-zinc-300 bg-white text-zinc-800 hover:border-emerald-400 hover:bg-emerald-50/40"
                  }`}
                >
                  <span>{opt.label}</span>
                  {wouldBuyAgain === opt.value ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border border-zinc-300 transition-colors group-hover:border-emerald-400" />
                  )}
                </button>
              ))}
            </div>
          </Question>

          {/* Q6 — optional. No required attr. No validation. */}
          <Question
            num="06"
            label="One thing you'd tell a peer evaluating it?"
            hint="Optional &mdash; skip if you're in a hurry"
            testId="q6-block"
            optional
          >
            <input
              data-testid="q6-peer-advice-input"
              type="text"
              value={peerAdvice}
              onChange={(e) => setPeerAdvice(e.target.value)}
              placeholder="The single thing they should know…"
              maxLength={500}
              className="w-full border border-zinc-300 bg-white px-4 py-4 font-display text-lg text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
            />
          </Question>

          {/* Email */}
          <div
            data-testid="email-block"
            className="border-t border-zinc-200 pt-10"
          >
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-emerald-600">
              // delivery
            </p>
            <label className="mt-3 block font-display text-2xl font-semibold text-zinc-900 sm:text-3xl">
              Where should we send the full benchmark?
            </label>
            <input
              data-testid="benchmark-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-5 w-full border border-zinc-900 bg-white px-5 py-5 font-display text-xl text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500 sm:py-6"
            />
          </div>

          {/* Submit */}
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
              Contributors get the full report before it&apos;s public.
            </p>
            <button
              type="submit"
              data-testid="benchmark-submit-btn"
              disabled={!canSubmit || submitting}
              className="group inline-flex items-center justify-between gap-4 border border-zinc-900 bg-zinc-900 px-6 py-4 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>{submitting ? "Submitting…" : "Count me in"}</span>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

// ---- Sub-components ----

const Question = ({ num, label, hint, testId, optional, children }) => (
  <div data-testid={testId} className="flex flex-col gap-4">
    <div className="flex items-baseline justify-between gap-4">
      <h3 className="font-display text-xl font-semibold leading-snug text-zinc-900 sm:text-2xl">
        <span className="mr-3 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-emerald-600">
          Q{num}
        </span>
        {label}
      </h3>
      {optional && (
        <span className="shrink-0 border border-zinc-300 bg-white px-2 py-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Optional
        </span>
      )}
    </div>
    {hint && (
      <p
        className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500"
        dangerouslySetInnerHTML={{ __html: hint }}
      />
    )}
    <div>{children}</div>
  </div>
);

const ChipGrid = ({ options, selected, onToggle, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isActive = selected.includes(opt.value);
      return (
        <button
          key={opt.value}
          type="button"
          data-testid={`${testIdPrefix}-${opt.value}`}
          onClick={() => onToggle(opt.value)}
          className={`inline-flex items-center gap-2 border px-3.5 py-2.5 font-mono-tech text-[11px] uppercase tracking-[0.18em] transition-colors ${
            isActive
              ? "border-emerald-600 bg-emerald-50 text-zinc-900"
              : "border-zinc-300 bg-white text-zinc-700 hover:border-emerald-400 hover:bg-emerald-50/40"
          }`}
        >
          {isActive && <Check className="h-3.5 w-3.5 text-emerald-600" />}
          {opt.label}
        </button>
      );
    })}
  </div>
);

const RatingScale = ({ value, onChange, testIdPrefix }) => (
  <div className="flex flex-col gap-3">
    <div className="grid grid-cols-5 gap-2">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            data-testid={`${testIdPrefix}-rating-${n}`}
            onClick={() => onChange(n)}
            className={`flex flex-col items-center gap-1 border py-4 transition-colors ${
              active
                ? "border-emerald-600 bg-emerald-50 text-zinc-900"
                : "border-zinc-300 bg-white text-zinc-800 hover:border-emerald-400 hover:bg-emerald-50/40"
            }`}
          >
            <span className="font-display text-2xl font-bold sm:text-3xl">{n}</span>
            <span className="font-mono-tech text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              {RATING_LABELS[n] || ""}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);
